/**
 * Input modality for focus rings (owner review, 08/10).
 *
 * Chrome turns `:focus-visible` on for the focused element on ANY keydown —
 * Cmd/Ctrl/Shift alone included — so a container or a control focused by a
 * click or by code grew a white ring as soon as the reader pressed Cmd. Rings
 * therefore depend on how the reader is navigating, tracked once here:
 *
 *   pointerdown                                   → 'pointer'
 *   Tab / arrows / Home / End / PageUp / PageDown → 'keyboard'
 *   Enter / Space / Escape outside a text field   → 'keyboard'
 *   modifiers alone, Cmd/Ctrl/Alt combos, typing  → unchanged
 *
 * The value lives on `<html data-octo-modality>` (menus and the launcher are
 * outside the chat root: portals / app header) and is mirrored on every
 * registered chat root. Ring helpers draw only under
 * `html[data-octo-modality="keyboard"]` AND `:focus-visible`.
 */
import { useEffect } from 'react';

export type InputModality = 'keyboard' | 'pointer';

export const OCTO_MODALITY_ATTR = 'data-octo-modality';

const NAV_KEYS = new Set([
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);
const ACTIVATION_KEYS = new Set(['Enter', ' ', 'Escape']);

type KeyLike = {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  target?: EventTarget | null;
};

const isTextField = (target: EventTarget | null | undefined): boolean => {
  if (!target || typeof (target as Element).tagName !== 'string') return false;
  const el = target as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === 'textarea') return true;
  if (tag === 'input') {
    const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
    return !['button', 'checkbox', 'radio', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
  }
  return el.isContentEditable === true;
};

/** 'keyboard' when this keydown is keyboard NAVIGATION; null = leave the modality as is. */
export function modalityForKey(e: KeyLike): InputModality | null {
  // Modifiers alone (key "Meta", "Control", "Shift", "Alt", "CapsLock") and
  // shortcuts (Cmd+C, Ctrl+J…) never mean "I'm navigating by keyboard".
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  if (e.key === 'Tab') return 'keyboard';
  // Inside a text field arrows/Home/End move the caret and Enter/Space type.
  if (isTextField(e.target)) return null;
  if (NAV_KEYS.has(e.key) || ACTIVATION_KEYS.has(e.key)) return 'keyboard';
  return null;
}

let current: InputModality | null = null;
let installed = false;
const roots = new Set<HTMLElement>();

const apply = (m: InputModality) => {
  if (current === m) return;
  current = m;
  if (typeof document !== 'undefined') document.documentElement.setAttribute(OCTO_MODALITY_ATTR, m);
  roots.forEach((r) => r.setAttribute(OCTO_MODALITY_ATTR, m));
};

function ensureTracking(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener(
    'keydown',
    (e) => {
      const m = modalityForKey(e);
      if (m) apply(m);
    },
    true,
  );
  window.addEventListener('pointerdown', () => apply('pointer'), true);
}

/** Current modality (null before any navigation input). */
export const getInputModality = (): InputModality | null => current;

/** True when keyboard focus should be drawn right now. */
export const isKeyboardModality = (): boolean => current === 'keyboard';

/**
 * Installs the (single, global) tracker and mirrors the value on `root`.
 * Safe to call from several components.
 */
export function useInputModality(
  root?: React.RefObject<HTMLElement | null>,
  /** Re-register when this changes (e.g. the root mounts on first open). */
  mountKey?: unknown,
): void {
  useEffect(() => {
    ensureTracking();
    const el = root?.current;
    if (!el) return undefined;
    roots.add(el);
    if (current) el.setAttribute(OCTO_MODALITY_ATTR, current);
    return () => {
      roots.delete(el);
    };
  }, [root, mountKey]);
}

/** Test hook: forget the modality (and the attribute). */
export function resetInputModalityForTests(): void {
  current = null;
  if (typeof document !== 'undefined') document.documentElement.removeAttribute(OCTO_MODALITY_ATTR);
  roots.forEach((r) => r.removeAttribute(OCTO_MODALITY_ATTR));
}
